import React, { useEffect, useMemo, useState } from 'react'
import Navbar from '../../components/client/Navbar'
import Popup from '../../components/client/Popup'
import { useNavigate } from 'react-router-dom'

/**
 * CreateInstancePage — Polished two-tab provisioning flow
 * - Tab "Standard": Location → Product → Image/OS → Plan → Auth → Add‑ons → Summary
 * - Tab "Quick OS": Image/OS → (optional Auth) → Name/Add‑ons → Summary
 *
 * Design goals
 * - Clear information hierarchy with SectionCard wrappers
 * - Consistent spacing/typography, better contrast
 * - A11y: labeled controls, aria attributes, logical tab order
 * - Responsive: grid adapts; right sidebar sticks on wide viewports
 * - Defensive UX: inline validation, disabled primary action until valid
 */

// ---------------------- MOCK DATA ----------------------
const MOCK_DATA = {
  credits: 150,
  categories: [
    { id: 'dedi-cpu', name: 'Dedicated CPU' },
    { id: 'compute', name: 'Cloud Compute' },
    { id: 'high-perf', name: 'High Performance' },
    { id: 'storage', name: 'Storage Optimized' },
  ],
  regions: [
    { id: 'sgp', name: 'Singapore', country: 'SG' },
    { id: 'hkg', name: 'Hong Kong', country: 'HK' },
    { id: 'tyo', name: 'Tokyo', country: 'JP' },
    { id: 'fra', name: 'Frankfurt', country: 'DE' },
    { id: 'syd', name: 'Sydney', country: 'AU' },
    { id: 'sjc', name: 'Silicon Valley', country: 'US' },
  ],
  images: {
    distribution: [
      { id: 'ubuntu-22', name: 'Ubuntu 22.04 LTS' },
      { id: 'debian-12', name: 'Debian 12' },
      { id: 'rocky-9', name: 'Rocky Linux 9' },
      { id: 'windows-2022', name: 'Windows Server 2022' },
    ],
    marketplace: [
      { id: 'docker', name: 'Docker CE' },
      { id: 'lamp', name: 'LAMP' },
      { id: 'node', name: 'Node.js' },
    ],
    my_images: [{ id: 'snap-101', name: 'snapshot-2025-08-01' }],
    iso: [{ id: 'iso-ubuntu-24', name: 'Ubuntu 24.04 ISO' }],
  },
  plans: [
    // Dedicated
    {
      id: 'd-1',
      category: 'dedi-cpu',
      label: 'VC1-1C-1G',
      cpu: 1,
      ramGB: 1,
      ssdGB: 25,
      bandwidthTB: 1,
      priceMonthly: 5,
      priceHourly: 0.0075,
    },
    { id: 'd-2', category: 'dedi-cpu', label: 'VC2-1C-2G', cpu: 1, ramGB: 2, ssdGB: 55, bandwidthTB: 2, priceMonthly: 10, priceHourly: 0.015 },
    { id: 'd-3', category: 'dedi-cpu', label: 'VC3-2C-4G', cpu: 2, ramGB: 4, ssdGB: 80, bandwidthTB: 3, priceMonthly: 20, priceHourly: 0.03 },
    // Cloud Compute
    { id: 'c-1', category: 'compute', label: 'VC1-1C-1G', cpu: 1, ramGB: 1, ssdGB: 25, bandwidthTB: 1, priceMonthly: 5, priceHourly: 0.0075 },
    { id: 'c-2', category: 'compute', label: 'VC2-1C-2G', cpu: 1, ramGB: 2, ssdGB: 55, bandwidthTB: 2, priceMonthly: 10, priceHourly: 0.015 },
    { id: 'c-3', category: 'compute', label: 'VC3-2C-4G', cpu: 2, ramGB: 4, ssdGB: 80, bandwidthTB: 3, priceMonthly: 20, priceHourly: 0.03 },
    // High Performance
    { id: 'hp-1', category: 'high-perf', label: 'HP-2C-4G', cpu: 2, ramGB: 4, ssdGB: 80, bandwidthTB: 3, priceMonthly: 28, priceHourly: 0.042 },
    { id: 'hp-2', category: 'high-perf', label: 'HP-4C-8G', cpu: 4, ramGB: 8, ssdGB: 160, bandwidthTB: 4, priceMonthly: 48, priceHourly: 0.072 },
    // Storage Optimized
    { id: 'st-1', category: 'storage', label: 'ST-2C-8G-300G', cpu: 2, ramGB: 8, ssdGB: 300, bandwidthTB: 3, priceMonthly: 30, priceHourly: 0.045 },
    { id: 'st-2', category: 'storage', label: 'ST-4C-16G-600G', cpu: 4, ramGB: 16, ssdGB: 600, bandwidthTB: 4, priceMonthly: 60, priceHourly: 0.09 },
  ],
  addOns: {
    ha: { label: 'High Availability', monthly: 5, hourly: 0.0075 },
    floatingIp: { label: 'Floating IP', monthly: 1, hourly: 0.0015 },
    backups: { label: 'Automatic Backups', monthly: 2, hourly: 0.003 },
  },
}

const currency = (n) => `$${Number(n).toFixed(2)}`
const cx = (...cls) => cls.filter(Boolean).join(' ')

// ---------------------- Small UI primitives ----------------------
const SectionCard = ({ title, subtitle, children, right = null, id }) => (
  <section
    id={id}
    className="bg-black/40 rounded-2xl border border-gray-700 p-6 shadow-[0_0_0_1px_rgba(148,163,184,0.12)]"
  >
    <div className="flex items-start justify-between gap-4 mb-4">
      <div>
        <h2 className="text-base md:text-lg font-semibold tracking-tight">{title}</h2>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
      {right}
    </div>
    {children}
  </section>
)

const Segmented = ({ value, onChange, options, ariaLabel }) => (
  <div role="tablist" aria-label={ariaLabel} className="inline-flex rounded-xl overflow-hidden border border-gray-700">
    {options.map((opt) => (
      <button
        key={opt.value}
        role="tab"
        aria-selected={value === opt.value}
        onClick={() => onChange(opt.value)}
        className={cx(
          'px-4 py-2 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
          value === opt.value ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-200 hover:bg-gray-700'
        )}
      >
        {opt.label}
      </button>
    ))}
  </div>
)

const Labeled = ({ label, hint, children, id }) => (
  <label htmlFor={id} className="block">
    <span className="block text-sm font-medium text-gray-200">{label}</span>
    {hint && <span className="block text-xs text-gray-400 mb-1">{hint}</span>}
    {children}
  </label>
)

// ---------------------- Main Component ----------------------
export default function CreateInstancePage() {
  const navigate = useNavigate()

  // Tabs
  const [activeTab, setActiveTab] = useState('standard') // 'standard' | 'quick'

  // Core selections (shared across tabs)
  const [name, setName] = useState('')
  const [category, setCategory] = useState(MOCK_DATA.categories[0].id)
  const [region, setRegion] = useState(MOCK_DATA.regions[0].id)
  const [imageTab, setImageTab] = useState('distribution')
  const [image, setImage] = useState(MOCK_DATA.images.distribution[0].id)
  const [planId, setPlanId] = useState(
    MOCK_DATA.plans.find((p) => p.category === MOCK_DATA.categories[0].id)?.id || ''
  )
  const [options, setOptions] = useState({ ha: false, floatingIp: false, backups: false })
  const [authMethod, setAuthMethod] = useState('ssh')
  const [sshKey, setSshKey] = useState('default-key')
  const [popup, setPopup] = useState(null)

  // Controls (plans list)
  const [planView, setPlanView] = useState('cards') // 'cards' | 'table'
  const [planQuery, setPlanQuery] = useState('')
  const [planSort, setPlanSort] = useState('priceAsc') // priceAsc | priceDesc | cpuDesc | ramDesc

  // Derived: plans in selected category
  const plans = useMemo(() => MOCK_DATA.plans.filter((p) => p.category === category), [category])

  const filteredPlans = useMemo(() => {
    const q = planQuery.trim().toLowerCase()
    let list = !q
      ? [...plans]
      : plans.filter((p) => [p.label, p.cpu, p.ramGB, p.ssdGB].join(' ').toLowerCase().includes(q))

    switch (planSort) {
      case 'priceDesc':
        list.sort((a, b) => b.priceMonthly - a.priceMonthly)
        break
      case 'cpuDesc':
        list.sort((a, b) => b.cpu - a.cpu || b.ramGB - a.ramGB)
        break
      case 'ramDesc':
        list.sort((a, b) => b.ramGB - a.ramGB || b.cpu - a.cpu)
        break
      default:
        list.sort((a, b) => a.priceMonthly - b.priceMonthly)
    }

    return list
  }, [plans, planQuery, planSort])

  // Keep valid plan on category change
  useEffect(() => {
    if (!plans.some((p) => p.id === planId)) {
      setPlanId(plans[0]?.id || '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category])

  const selectedPlan = useMemo(() => plans.find((p) => p.id === planId) || null, [plans, planId])

  // Pricing
  const price = useMemo(() => {
    const baseMonthly = selectedPlan?.priceMonthly || 0
    const baseHourly = selectedPlan?.priceHourly || 0
    const addMonthly = Object.entries(options).reduce((sum, [k, v]) => (v ? sum + (MOCK_DATA.addOns[k]?.monthly || 0) : sum), 0)
    const addHourly = Object.entries(options).reduce((sum, [k, v]) => (v ? sum + (MOCK_DATA.addOns[k]?.hourly || 0) : sum), 0)
    return { monthly: baseMonthly + addMonthly, hourly: baseHourly + addHourly }
  }, [selectedPlan, options])

  // Images by tab
  const imagesByTab = useMemo(() => {
    switch (imageTab) {
      case 'distribution':
        return MOCK_DATA.images.distribution
      case 'marketplace':
        return MOCK_DATA.images.marketplace
      case 'my-images':
        return MOCK_DATA.images.my_images
      case 'iso':
        return MOCK_DATA.images.iso
      default:
        return []
    }
  }, [imageTab])

  // Handlers
  const toggleOption = (key) => setOptions((prev) => ({ ...prev, [key]: !prev[key] }))

  const isValidStandard = image && planId && (authMethod === 'password' || (authMethod === 'ssh' && sshKey))
  const isValidQuick = image && (authMethod === 'password' || (authMethod === 'ssh' && sshKey))

  const handleCreate = async () => {
    const valid = activeTab === 'standard' ? isValidStandard : isValidQuick
    if (!valid) {
      setPopup({ message: '❌ Please fill all required fields.', type: 'error' })
      return
    }

    // MOCK submit
    const payload = {
      name: name || 'my-instance',
      region,
      image_id: image,
      flavor_id: activeTab === 'standard' ? planId : undefined,
      options,
      auth: { method: authMethod, sshKey: authMethod === 'ssh' ? sshKey : undefined },
      estimate: price,
    }

    // eslint-disable-next-line no-console
    console.log('[MOCK SUBMIT]', payload)
    setPopup({ message: '✅ Instance created (mock). Replace with real API calls later.', type: 'success' })
  }

  return (
    <div className="bg-white dark:bg-dark-gradient from-black via-gray-900 to-gray-800 text-gray-100 min-h-screen">
      <Navbar credits={MOCK_DATA.credits} />
      {popup && <Popup message={popup.message} type={popup.type} onClose={() => setPopup(null)} />}

      {/* Container */}
      <div className="max-w-[1600px] mx-auto p-6 md:p-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-blue-500">
            Deploy New Instance
          </h1>

          {/* Tabs Switcher */}
          <Segmented
            ariaLabel="Provisioning mode"
            value={activeTab}
            onChange={setActiveTab}
            options={[
              { value: 'standard', label: 'Standard' },
              { value: 'quick', label: 'Quick OS' },
            ]}
          />
        </div>

        {activeTab === 'standard' ? (
          // ================= TAB: STANDARD =================
          <div className="grid grid-cols-1 2xl:grid-cols-5 gap-6">
            {/* LEFT: main steps */}
            <div className="2xl:col-span-4 space-y-6">
              {/* 1) Location */}
              <SectionCard title="1) Location" subtitle="Choose where to deploy for best latency" id="step-location">
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                  {MOCK_DATA.regions.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      className={cx(
                        'text-left border rounded-xl p-3 transition hover:shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                        region === r.id ? 'border-blue-500 ring-2 ring-blue-500/30' : 'border-gray-700 hover:border-gray-500'
                      )}
                      aria-pressed={region === r.id}
                      onClick={() => setRegion(r.id)}
                    >
                      <div className="text-xs text-gray-400">{r.country}</div>
                      <div className="font-semibold">{r.name}</div>
                      <div className="text-[11px] text-gray-500">Low latency</div>
                    </button>
                  ))}
                </div>
              </SectionCard>

              {/* 2) Product */}
              <SectionCard title="2) Product" subtitle="Pick a compute class" id="step-product">
                <div className="flex flex-wrap gap-2">
                  {MOCK_DATA.categories.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      className={cx(
                        'px-4 py-2 rounded-full text-sm font-medium transition border focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                        category === cat.id ? 'bg-blue-600 border-blue-500' : 'bg-gray-800 border-gray-700 hover:bg-gray-700'
                      )}
                      aria-pressed={category === cat.id}
                      onClick={() => setCategory(cat.id)}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </SectionCard>

              {/* 3) Image / OS */}
              <SectionCard title="3) Image / OS" subtitle="Choose a base image or marketplace app" id="step-image">
                <div className="flex gap-2 border-b border-gray-700 pb-3 mb-4 overflow-x-auto" role="tablist" aria-label="Image source">
                  {['distribution', 'marketplace', 'my-images', 'iso'].map((tab) => (
                    <button
                      key={tab}
                      role="tab"
                      aria-selected={imageTab === tab}
                      type="button"
                      className={cx(
                        'px-4 py-2 rounded-full text-sm font-medium transition whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                        imageTab === tab ? 'bg-blue-600' : 'bg-gray-800 hover:bg-gray-700'
                      )}
                      onClick={() => setImageTab(tab)}
                    >
                      {tab.replace('-', ' ').toUpperCase()}
                    </button>
                  ))}
                </div>

                {imagesByTab.length === 0 ? (
                  <p className="text-gray-400">No images available in this category.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {imagesByTab.map((img) => (
                      <label key={img.id} className={cx('flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition', image === img.id ? 'border-blue-500 bg-blue-500/10' : 'border-gray-700 hover:border-gray-500')}>
                        <input
                          type="radio"
                          name="image-os"
                          value={img.id}
                          checked={image === img.id}
                          onChange={() => setImage(img.id)}
                          className="accent-blue-500"
                        />
                        <span>{img.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </SectionCard>

              {/* 4) Plan */}
              <SectionCard
                title="4) Plan"
                subtitle="Select a flavor that matches your workload"
                id="step-plan"
                right={
                  <Segmented
                    ariaLabel="Plan view"
                    value={planView}
                    onChange={setPlanView}
                    options={[
                      { value: 'cards', label: 'Cards' },
                      { value: 'table', label: 'Table' },
                    ]}
                  />
                }
              >
                {/* Meta */}
                <div className="flex flex-wrap items-center justify-between text-xs text-gray-400 mb-3 gap-2">
                  <span>
                    Category:{' '}
                    {MOCK_DATA.categories.find((c) => c.id === category)?.name}
                  </span>
                  <span>{plans.length} options</span>
                </div>

                {/* Controls */}
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 mb-3">
                  <input
                    type="text"
                    value={planQuery}
                    onChange={(e) => setPlanQuery(e.target.value)}
                    placeholder="Search by label / CPU / RAM / SSD"
                    className="flex-1 px-3 py-2 rounded-lg bg-gray-900 border border-gray-700 text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
                    aria-label="Search plans"
                  />
                  <select
                    value={planSort}
                    onChange={(e) => setPlanSort(e.target.value)}
                    className="px-3 py-2 rounded-lg bg-gray-900 border border-gray-700 text-sm focus:outline-none focus:border-blue-500"
                    aria-label="Sort plans"
                  >
                    <option value="priceAsc">Price ↑</option>
                    <option value="priceDesc">Price ↓</option>
                    <option value="cpuDesc">CPU ↓</option>
                    <option value="ramDesc">RAM ↓</option>
                  </select>
                </div>

                {/* List */}
                {planView === 'table' ? (
                  <div className="max-h-[60vh] 2xl:max-h-[28rem] overflow-auto rounded-xl border border-gray-800">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-900">
                        <tr className="text-left text-gray-300">
                          <th className="px-3 py-2 font-medium">Label</th>
                          <th className="px-3 py-2 font-medium">CPU</th>
                          <th className="px-3 py-2 font-medium hidden md:table-cell">RAM</th>
                          <th className="px-3 py-2 font-medium hidden lg:table-cell">SSD</th>
                          <th className="px-3 py-2 font-medium hidden lg:table-cell">Bandwidth</th>
                          <th className="px-3 py-2 font-medium">Price</th>
                          <th className="px-3 py-2 w-28"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPlans.map((p) => (
                          <tr key={p.id} className="border-t border-gray-800 hover:bg-gray-800/50">
                            <td className="px-3 py-2">{p.label}</td>
                            <td className="px-3 py-2">{p.cpu} vCPU</td>
                            <td className="px-3 py-2 hidden md:table-cell">{p.ramGB} GB</td>
                            <td className="px-3 py-2 hidden lg:table-cell">{p.ssdGB} GB</td>
                            <td className="px-3 py-2 hidden lg:table-cell">{p.bandwidthTB} TB</td>
                            <td className="px-3 py-2 font-semibold">{currency(p.priceMonthly)}/mo</td>
                            <td className="px-3 py-2 text-right w-28">
                              <button
                                type="button"
                                onClick={() => setPlanId(p.id)}
                                className={cx(
                                  'px-3 py-1 rounded-md border text-xs',
                                  planId === p.id ? 'border-blue-500 text-blue-300' : 'border-gray-600 text-gray-200 hover:border-gray-400'
                                )}
                                aria-pressed={planId === p.id}
                              >
                                {planId === p.id ? 'Selected' : 'Choose'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-1 gap-3 max-h-[60vh] 2xl:max-h-[28rem] overflow-y-auto pr-1">
                    {filteredPlans.map((p) => (
                      <label key={p.id} className={cx('relative border rounded-xl p-4 w-full cursor-pointer transition hover:shadow', planId === p.id ? 'border-blue-500 ring-2 ring-blue-500/30' : 'border-gray-700 hover:border-gray-500')}>
                        <input
                          type="radio"
                          name="plan"
                          className="hidden"
                          value={p.id}
                          checked={planId === p.id}
                          onChange={() => setPlanId(p.id)}
                        />
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-semibold truncate">{p.label}</div>
                            <div className="text-xs text-gray-400 mt-1">
                              {p.cpu} vCPU • {p.ramGB}GB RAM • {p.ssdGB}GB SSD
                            </div>
                            <div className="text-[11px] text-gray-500">{p.bandwidthTB}TB transfer</div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="text-lg font-bold">{currency(p.priceMonthly)}/mo</div>
                            <div className="text-xs text-gray-500">~${p.priceHourly.toFixed(4)}/hr</div>
                          </div>
                        </div>
                      </label>
                    ))}
                    {filteredPlans.length === 0 && <div className="text-sm text-gray-400">No matching plans.</div>}
                  </div>
                )}
              </SectionCard>

              {/* 5) Authentication */}
              <SectionCard title="5) Authentication" subtitle="Use an SSH key (recommended) or password" id="step-auth">
                <div className="flex flex-wrap items-center gap-6 mb-3">
                  {['ssh', 'password'].map((method) => (
                    <label key={method} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="authMethod"
                        value={method}
                        checked={authMethod === method}
                        onChange={() => setAuthMethod(method)}
                        className="accent-blue-500"
                      />
                      <span className="uppercase text-sm">{method}</span>
                    </label>
                  ))}
                </div>
                {authMethod === 'ssh' ? (
                  <Labeled label="SSH Key" hint="Select a saved key" id="ssh-key">
                    <select
                      id="ssh-key"
                      value={sshKey}
                      onChange={(e) => setSshKey(e.target.value)}
                      className="mt-1 border border-gray-700 rounded px-4 py-2 w-full bg-gray-800 text-gray-100 focus:outline-none focus:border-blue-500"
                    >
                      <option value="default-key">default-key</option>
                      <option value="admin-key">admin-key</option>
                    </select>
                  </Labeled>
                ) : (
                  <Labeled label="Root password" hint="Use a strong, unique password" id="root-pass">
                    <input
                      id="root-pass"
                      type="password"
                      placeholder="Enter a strong password"
                      className="mt-1 border border-gray-700 rounded px-4 py-2 w-full bg-gray-800 text-gray-100 placeholder-gray-500 focus:outline-none focus:border-blue-500"
                    />
                  </Labeled>
                )}
              </SectionCard>
            </div>

            {/* RIGHT: sticky summary & misc */}
            <aside className="2xl:col-span-1 space-y-6 2xl:sticky 2xl:top-6 self-start">
              {/* Name */}
              <SectionCard title="Instance Name" subtitle="A-z, 0-9, hyphen" id="step-name">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., nebula-x01"
                  className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                  aria-invalid={name.trim() === ''}
                />
                {name.trim() === '' && <p className="text-xs text-amber-400 mt-2">Name is optional now and will default to <code>my-instance</code>.</p>}
              </SectionCard>

              {/* Add-ons */}
              <SectionCard title="Add-ons" subtitle="Enhance reliability & flexibility" id="step-addons">
                <div className="space-y-2">
                  {Object.entries(MOCK_DATA.addOns).map(([key, spec]) => (
                    <label key={key} className="flex items-center justify-between gap-2 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <input type="checkbox" className="accent-blue-500" checked={options[key]} onChange={() => toggleOption(key)} />
                        <span>{spec.label}</span>
                      </div>
                      <div className="text-sm text-gray-400">{currency(spec.monthly)}/mo</div>
                    </label>
                  ))}
                </div>
              </SectionCard>

              {/* Summary */}
              <SectionCard title="Summary" subtitle="Review your selections">
                <ul className="text-sm text-gray-300 space-y-1">
                  <li><span className="text-gray-400">Product:</span> {MOCK_DATA.categories.find((c) => c.id === category)?.name}</li>
                  <li><span className="text-gray-400">Region:</span> {MOCK_DATA.regions.find((r) => r.id === region)?.name}</li>
                  <li><span className="text-gray-400">Image:</span> {image}</li>
                  <li>
                    <span className="text-gray-400">Plan:</span> {selectedPlan ? `${selectedPlan.label} • ${selectedPlan.cpu}vCPU/${selectedPlan.ramGB}GB/${selectedPlan.ssdGB}GB` : '—'}
                  </li>
                </ul>

                <div className="mt-4 p-4 rounded-xl bg-gray-900 border border-gray-700">
                  <div className="flex items-center justify-between">
                    <span>Estimated Monthly</span>
                    <strong className="text-blue-400">{currency(price.monthly)}/mo</strong>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-400 mt-1">
                    <span>Estimated Hourly</span>
                    <span>~${price.hourly.toFixed(4)}/hr</span>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    className="px-4 py-2 border border-gray-600 rounded text-gray-300 hover:border-gray-400 hover:text-white transition duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-1/2"
                    onClick={() => navigate('/dashboard')}
                    aria-label="Go back to dashboard"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleCreate}
                    disabled={!isValidStandard}
                    className={cx(
                      'px-5 py-2 font-semibold rounded-lg transition shadow-md w-1/2',
                      isValidStandard ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                    )}
                  >
                    🚀 Create Instance
                  </button>
                </div>

                <p className="text-xs text-gray-500 mt-3">This is a mock flow. Replace MOCK_DATA and handleCreate with real API calls when ready.</p>
              </SectionCard>
            </aside>
          </div>
        ) : (
          // ================= TAB: QUICK OS =================
          <div className="grid grid-cols-1 2xl:grid-cols-5 gap-6">
            {/* LEFT: image + auth */}
            <div className="2xl:col-span-4 space-y-6">
              <SectionCard title="Choose Operating System / App" subtitle="Minimal setup" id="qos-image">
                <div className="flex gap-2 border-b border-gray-700 pb-3 mb-4 overflow-x-auto" role="tablist" aria-label="Image source">
                  {['distribution', 'marketplace', 'my-images', 'iso'].map((tab) => (
                    <button
                      key={tab}
                      role="tab"
                      aria-selected={imageTab === tab}
                      type="button"
                      className={cx(
                        'px-4 py-2 rounded-full text-sm font-medium transition whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                        imageTab === tab ? 'bg-blue-600' : 'bg-gray-800 hover:bg-gray-700'
                      )}
                      onClick={() => setImageTab(tab)}
                    >
                      {tab.replace('-', ' ').toUpperCase()}
                    </button>
                  ))}
                </div>

                {imagesByTab.length === 0 ? (
                  <p className="text-gray-400">No images available in this category.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {imagesByTab.map((img) => (
                      <label key={img.id} className={cx('flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition', image === img.id ? 'border-blue-500 bg-blue-500/10' : 'border-gray-700 hover:border-gray-500')}>
                        <input type="radio" name="image-os2" value={img.id} checked={image === img.id} onChange={() => setImage(img.id)} className="accent-blue-500" />
                        <span>{img.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </SectionCard>

              {/* Minimal Auth */}
              <SectionCard title="Authentication" subtitle="Optional but recommended" id="qos-auth">
                <div className="flex gap-6 mb-3">
                  {['ssh', 'password'].map((method) => (
                    <label key={method} className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="authMethod2" value={method} checked={authMethod === method} onChange={() => setAuthMethod(method)} className="accent-blue-500" />
                      <span className="uppercase text-sm">{method}</span>
                    </label>
                  ))}
                </div>
                {authMethod === 'ssh' ? (
                  <select value={sshKey} onChange={(e) => setSshKey(e.target.value)} className="mt-1 border border-gray-700 rounded px-4 py-2 w-full bg-gray-800 text-gray-100 focus:outline-none focus:border-blue-500">
                    <option value="default-key">default-key</option>
                    <option value="admin-key">admin-key</option>
                  </select>
                ) : (
                  <input type="password" placeholder="Enter a strong password" className="mt-1 border border-gray-700 rounded px-4 py-2 w-full bg-gray-800 text-gray-100 placeholder-gray-500 focus:outline-none focus:border-blue-500" />
                )}
              </SectionCard>
            </div>

            {/* RIGHT: quick summary */}
            <aside className="2xl:col-span-1 space-y-6 2xl:sticky 2xl:top-6 self-start">
              <SectionCard title="Instance Name" subtitle="A-z, 0-9, hyphen">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., nebula-x01"
                  className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                />
              </SectionCard>

              <SectionCard title="Add-ons" subtitle="Enhance reliability & flexibility">
                <div className="space-y-2">
                  {Object.entries(MOCK_DATA.addOns).map(([key, spec]) => (
                    <label key={key} className="flex items-center justify-between gap-2 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <input type="checkbox" className="accent-blue-500" checked={options[key]} onChange={() => toggleOption(key)} />
                        <span>{spec.label}</span>
                      </div>
                      <div className="text-sm text-gray-400">{currency(spec.monthly)}/mo</div>
                    </label>
                  ))}
                </div>
              </SectionCard>

              <SectionCard title="Summary" subtitle="Review your selections">
                <ul className="text-sm text-gray-300 space-y-1">
                  <li><span className="text-gray-400">Product:</span> {MOCK_DATA.categories.find((c) => c.id === category)?.name}</li>
                  <li><span className="text-gray-400">Region:</span> {MOCK_DATA.regions.find((r) => r.id === region)?.name}</li>
                  <li><span className="text-gray-400">Image:</span> {image}</li>
                  <li><span className="text-gray-400">Plan:</span> {selectedPlan ? `${selectedPlan.label} • ${selectedPlan.cpu}vCPU/${selectedPlan.ramGB}GB/${selectedPlan.ssdGB}GB` : '—'}</li>
                </ul>

                <div className="mt-4 p-4 rounded-xl bg-gray-900 border border-gray-700">
                  <div className="flex items-center justify-between">
                    <span>Estimated Monthly</span>
                    <strong className="text-blue-400">{currency(price.monthly)}/mo</strong>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-400 mt-1">
                    <span>Estimated Hourly</span>
                    <span>~${price.hourly.toFixed(4)}/hr</span>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    className="px-4 py-2 border border-gray-600 rounded text-gray-300 hover:border-gray-400 hover:text-white transition duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-1/2"
                    onClick={() => navigate('/dashboard')}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleCreate}
                    disabled={!isValidQuick}
                    className={cx(
                      'px-5 py-2 font-semibold rounded-lg transition shadow-md w-1/2',
                      isValidQuick ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                    )}
                  >
                    🚀 Create Instance
                  </button>
                </div>

                <p className="text-xs text-gray-500 mt-3">This is a mock flow. Replace MOCK_DATA and handleCreate with real API calls when ready.</p>
              </SectionCard>
            </aside>
          </div>
        )}
      </div>
    </div>
  )
}
